import DashboardFiltration from '../../components/siteDashboard/filtration';
import { Divider, LinearProgress, Box, Typography } from '@mui/material';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useState, useCallback, useRef, useEffect } from 'react';
import ListView from '../../components/siteDashboard/ListView';
import { isObjectEmpty } from '../../utils/isObjectEmpty';
import NoRecord from '../../components/NoRecord';
import axios from 'axios';
import '../../css/dashboard/dashboardApp.css';

export const SiteDashboard = () => {

  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [showResponsiveFilter, setShowResponsiveFilter] = useState(false);

  const [filters, setFilters] = useState(
    JSON.parse(localStorage.getItem('siteDashboardFilters')) || {
      debouncedSearch: '',
      selectedYear: '',
      selectedMonth: '',
    }
  );

  const defaultSortBy = {
    name: null,
      purchase_price: null,
      total_expenses: null,
  };

  const [sortBy, setSortBy] = useState(() => {
    const storedSortBy = JSON.parse(localStorage.getItem('siteDashboardSortBy')) || {};
    return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
  });

  const [sites, setSites] = useState([]);
  const [sitesFetched, setSitesFetched] = useState(0);
  const [totalSites, setTotalSites] = useState(0);
  const [filteredSitesCount, setFilteredSitesCount] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalPrices, setTotalPrices] = useState(0);

  const [offset, setOffset] = useState(0);
  const limit = 30;

  const filtersEmpty = isObjectEmpty(filters, ['selectedYear', 'selectedMonth']);

  useEffect(() => {
    setLoading(true)
  }, [filters, sortBy]);

  useEffect(() => {
    localStorage.setItem('siteDashboardFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('siteDashboardSortBy', JSON.stringify(sortBy));
  }, [sortBy]);

  // Scroll to top when filters change
  useEffect(() => {
    if (scrollRef.current) {
      setOffset(0);
      scrollRef.current.scrollTo(0);
    }
  }, [filters]);

  const fetchSites = useCallback(async () => {
    try {
      const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
      const { status, data } = await axios.get('site/dashboard', { params: { offset: adjustedOffset, limit, filters, sortBy } });
      if (status === 200 || status === 201) {
        // Create a copy of the current files array
        const updatedSites = [];

        // Update the fetched records in their correct positions
        data?.sites?.forEach((file, index) => { updatedSites[adjustedOffset + index] = file });
        // Update the state with the new files array
        setLoading(false);
        setSites(updatedSites);
        setTotalSites(data?.totalSites);
        setFilteredSitesCount(data?.filteredSitesCount);
        setTotalExpenses(data?.totalExpensesSum);
        setTotalPrices(data.totalMachinePriceSum)
      }
    } catch (error) {
      setLoading(false);
      console.error('Error Fetching sites in SiteDashboard:', error);
      CustomErrorLogger(error, 'fetch sites in sites dashboard page');
    }
  }, [offset, filters, sortBy]);

  useEffect(() => {
    fetchSites()
  }, [fetchSites]);

  return (
    <Box>
      {loading ?
        <Box>
          <DashboardFiltration
            filtersEmpty={filtersEmpty}
            filters={filters}
            setFilters={setFilters}
            totalSites={totalSites}
            filteredSitesCount={filteredSitesCount}
            sitesFetched={sitesFetched}
            showResponsiveFilter={showResponsiveFilter}
            setShowResponsiveFilter={setShowResponsiveFilter}
          />
          <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
        </Box> :
        filtersEmpty && sites?.length === 0 ?
          <NoRecord
            description="Aucun site ajouté pour le moment"
            buttonTitle="Ajouter un site"
          /> :
          <Box>
            <DashboardFiltration
              filtersEmpty={filtersEmpty}
              filters={filters}
              setFilters={setFilters}
              totalSites={totalSites}
              filteredSitesCount={filteredSitesCount}
              sitesFetched={sitesFetched}
              showResponsiveFilter={showResponsiveFilter}
              setShowResponsiveFilter={setShowResponsiveFilter}
            />

            <ListView
              scrollRef={scrollRef}
              sites={sites}
              sortBy={sortBy}
              setSortBy={setSortBy}
              setOffset={setOffset}
              filteredSitesCount={filteredSitesCount}
              setSitesFetched={setSitesFetched}
              showResponsiveFilter={showResponsiveFilter}
            />
            <Divider sx={{ marginTop: '20px', marginBottom: '20px' }} />
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Total prix d'achat</Typography>
              <Typography className='addInvoice_total_typo'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalPrices)}</Typography>
            </Box>
            <Box className='addInvoice_total_box'>
              <Typography className='addInvoice_total_typo'>Total dépenses</Typography>
              <Typography className='addInvoice_total_typo'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalExpenses)}</Typography>
            </Box>
          </Box>
      }
    </Box>
  );
}
